import { ROLE_DATA } from '../data/roles.js';
import { DEFAULT_NIGHT_ROLE_ORDER } from '../data/constants.js';
import { buildNightQueue } from './buildNightQueue.js';
import { generateId } from './utils.js';

/** Shuffle array in place */
function shuffled(arr) { return [...arr].sort(() => Math.random() - 0.5); }

/** Assign roles to shuffled active players */
export function assignRoles(activePlayers, roleConfig) {
  const { villagerCount, werewolfCount, specialRoles } = roleConfig;
  const players = shuffled(activePlayers);
  let idx = 0;
  const assignments = [];
  for (const roleId of specialRoles) {
    if (idx >= players.length) break;
    assignments.push({ playerId: players[idx].id, roleId });
    idx++;
  }
  for (let i = 0; i < werewolfCount; i++) {
    if (idx >= players.length) break;
    assignments.push({ playerId: players[idx].id, roleId: "werewolf" });
    idx++;
  }
  for (let i = 0; i < villagerCount; i++) {
    if (idx >= players.length) break;
    assignments.push({ playerId: players[idx].id, roleId: "villager" });
    idx++;
  }
  return assignments;
}

/** Build the initial GameSession from role assignments and settings */
export function buildGameSession(activePlayers, assignments, roleConfig, settings = {}) {
  const players = activePlayers.map(p => {
    const a = assignments.find(x => x.playerId === p.id);
    const roleId = a?.roleId || "villager";
    const role = ROLE_DATA[roleId];
    return {
      id: p.id, name: p.name,
      alive: true,
      baseRoleId: roleId,
      effectiveAlignment: role.alignment,
      statuses: [],
      loverId: null,
      voteCount: 0,
      deathNight: null,
      mayor: false,
      sheepCount: 0,
    };
  });

  const originalWolfIds = players
    .filter(p => p.effectiveAlignment === "werewolf")
    .map(p => p.id);

  let thief_extraCards = [];
  if (roleConfig.specialRoles.includes("thief")) {
    const pool = ["werewolf","villager","villager","witch","protector","seer","hunter"];
    thief_extraCards = shuffled(pool).slice(0, 2);
  }

  const gameSettings = {
    mayorEnabled: settings.mayorEnabled !== false,
    witchPoisonOnNight1: !!settings.witchPoisonOnNight1,
    fatherInfectionOnNight1: !!settings.fatherInfectionOnNight1,
    muteSeer: !!settings.muteSeer,
    flutePlayerConstantCount: settings.flutePlayerConstantCount !== false,
    flutePlayerCharmsPerNight: Math.max(1, settings.flutePlayerCharmsPerNight ?? 2),
    nightRoleOrder: settings.nightRoleOrder || DEFAULT_NIGHT_ROLE_ORDER,
    shepherdSheepCount: Math.max(0, settings.shepherdSheepCount ?? 3),
  };

  const sheepRemaining = gameSettings.shepherdSheepCount;
  const extraDisabledRoles = sheepRemaining <= 0 ? ["shepherd"] : [];
  const queue = buildNightQueue(players, 1, false, gameSettings.nightRoleOrder, extraDisabledRoles);

  return {
    players,
    phase: gameSettings.mayorEnabled ? "mayor_election" : "night",
    night: 1,
    day: 0,
    roundNightActions: queue,
    currentNightRoleIndex: 0,
    witch_usedSave: false,
    witch_usedPoison: false,
    witch_savedVictim: false,
    protector_lastProtected: null,
    fatherUsedInfection: false,
    infectionAttempted: false,
    pendingDeaths: [],
    nightVictim: null,
    winner: null,
    mayorId: null,
    currentDebateSpeaker: 0,
    cupidUsed: false,
    newDeaths: [],
    nightLog: [],
    dayRevealSource: "night",
    specialRoles: roleConfig.specialRoles,
    hunterPendingShot: false,
    revoteTiedIds: null,
    originalWolfIds,
    ravenMarkId: null,
    elder_usedSurvival: false,
    elderPenaltyActive: false,
    wildChild_roleModelId: null,
    bbw_extraVictimId: null,
    ww_killTargetId: null,
    fox_checkTargetId: null,
    sheepRemaining,
    thief_extraCards,
    gameSettings,
  };
}
